import {Injectable} from '@angular/core';
import {BehaviorSubject, filter} from 'rxjs';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {Breadcrumb} from '../entities/breadcrumb.entity';
import {Backend} from '@generated-backend-types';
import MultilingualAttributeData = Backend.App.Data.Meta.MultilingualAttributeData;
import {DateTime} from 'luxon';

@Injectable({providedIn: 'root'})
export class BreadcrumbService {
  breadcrumbs$: BehaviorSubject<Breadcrumb[]> = new BehaviorSubject<Breadcrumb[]>([]);

  constructor(private router: Router, private route: ActivatedRoute) {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        const breadcrumbs = this.buildBreadcrumbs(this.route.root);
        this.breadcrumbs$.next(breadcrumbs);
      });
  }

  private buildBreadcrumbs(
    route: ActivatedRoute,
    parentUrl: string = '',
    breadcrumbs: Breadcrumb[] = []
  ): Breadcrumb[] {
    const children: ActivatedRoute[] = route.children;

    if (children.length === 0) return breadcrumbs;

    for (const child of children) {
      const routeSnapshot = child.snapshot;

      // Build the segment path with params if any
      const segments = routeSnapshot.url.map(segment => {
        if (segment.path.startsWith(':')) {

          const paramName = segment.path.slice(1);

          return routeSnapshot.params[paramName];
        }
        return segment.path;
      });

      const currentSegment = segments.join('/');

      const fullUrl = currentSegment ? `${parentUrl}/${currentSegment}` : parentUrl;

      const rawBreadcrumbs: Breadcrumb[] = (routeSnapshot.data['breadcrumbs'] || routeSnapshot.data['breadcrumb']) ?? [];
      const breadcrumbArray = Array.isArray(rawBreadcrumbs) ? rawBreadcrumbs : [rawBreadcrumbs];

      for (const bc of breadcrumbArray) {
        let label = '';

        if (bc.dynamic && bc.dataKey) {
          for (const dataKey of bc.dataKey.split(',')) {
            const value = this.getValueByPath(routeSnapshot.data, dataKey);

            if (value && typeof value === 'object' && 'en' in value && 'de' in value) {
              label = label + ' ' + ((value as MultilingualAttributeData)['de'] ?? bc.label);
            } else if (value && typeof value === 'string' && DateTime.fromISO(value).isValid) {
              label = label + ' ' + (DateTime.fromISO(value).toFormat('dd.MM.yyyy'));
            } else if (dataKey === '-' || dataKey === '|') {
              label = label + ` ${dataKey} `;
            } else {
              label = label + ' ' + (typeof value === 'string' ? value : bc.label);
            }
          }

          label = label.trim();

        } else {
          label = bc.label;
        }

        let route = null;
        if (bc.route) {
          const segments: string[] = bc.route.split('/').map(
            (segment: string) => {
              if (
                segment.startsWith(':')
              ) {
                const paramName = segment.slice(1);
                return routeSnapshot.params[paramName];
              }
              return segment;
            }
          )
          route = segments.join('/');
        }

        breadcrumbs.push({
          label,
          route: route ?? undefined,
          translate: bc.translate !== false
        });
      }

      return this.buildBreadcrumbs(child, fullUrl, breadcrumbs);
    }

    return breadcrumbs;
  }

  private getValueByPath(data: unknown, path: string): unknown {
    return path.split('.').reduce(
      (acc: unknown, part: string) => {
        // @ts-expect-error indirect any
        return (acc && typeof acc === 'object' && part in acc ? typeof acc[part] === 'number' ? acc[part].toString() : acc[part] : acc as string)
      }, data
    );
  }
}
